// ─── Template : HUB DE FONCTIONS (Core) ───────────────────────────
// Présentation reprise de MoPCal : des RUBRIQUES titrées, chacune avec sa
// grille de cartes (2 colonnes, même largeur et même hauteur).
//   • accordéon PAR RUBRIQUE : replié, seules les fonctions accessibles sont
//     là ; « +n à débloquer » déplie les verrouillées, rangées au même endroit ;
//   • réorganisation (↕ dans l'en-tête) : ▲▼ déplacent un BLOC, et une carte
//     touchée se déplace dans son bloc avec ◀ ▶ — le glisser n'étant pas
//     fiable sur iOS. L'ordre (blocs + cartes) est mémorisé par appareil.
//
//   <HubTuiles titre="Famille" emoji="👪" cleOrdre="famille" sections={[
//     { cle:'collaboration', titre:'Collaboration', tuiles:[
//       { cle, emoji, titre, desc, actif, palier, badge, onPress } ] },
//   ]} />
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import PageHeader from './PageHeader';
import CarteHub from './CarteHub';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const CLE = (c) => `hub:${c}:ordre`;

// Ordre mémorisé d'abord, nouveautés ensuite : une carte ajoutée par une mise
// à jour apparaît toujours, jamais perdue.
const fusionner = (cles, memo = []) => [
  ...memo.filter(c => cles.includes(c)),
  ...cles.filter(c => !memo.includes(c)),
];

export default function HubTuiles({ titre, emoji, sections = [], enfants, cleOrdre }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [ordre, setOrdre] = useState(null);       // { blocs:[id], cartes:{id:[cle]} }
  const [reorg, setReorg] = useState(false);
  const [sel, setSel] = useState(null);           // carte sélectionnée (réorg)
  const [ouverts, setOuverts] = useState({});     // rubriques dépliées

  useEffect(() => {
    if (!cleOrdre) return;
    AsyncStorage.getItem(CLE(cleOrdre)).then(v => { if (v) setOrdre(JSON.parse(v)); }).catch(() => {});
  }, [cleOrdre]);
  const persister = (next) => {
    setOrdre(next);
    if (cleOrdre) AsyncStorage.setItem(CLE(cleOrdre), JSON.stringify(next)).catch(() => {});
  };

  // Rubriques effectives : ordre des blocs, puis ordre des cartes dans chacun.
  const base = sections.map((sec, i) => ({ ...sec, id: sec.cle || String(i) }));
  const parId = Object.fromEntries(base.map(r => [r.id, r]));
  const blocs = fusionner(base.map(r => r.id), ordre?.blocs).map(id => {
    const r = parId[id];
    const cles = fusionner((r.tuiles || []).map(x => x.cle), ordre?.cartes?.[id]);
    const parCle = Object.fromEntries((r.tuiles || []).map(x => [x.cle, x]));
    return { ...r, tuiles: cles.map(c => parCle[c]).filter(Boolean) };
  }).filter(r => r.tuiles.length);

  const deplacerBloc = (id, dir) => {
    const ids = blocs.map(r => r.id);
    const i = ids.indexOf(id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    persister({ blocs: ids, cartes: ordre?.cartes || {} });
  };
  const deplacerCarte = (rubId, cle, dir) => {
    const r = blocs.find(x => x.id === rubId);
    const cles = r.tuiles.map(x => x.cle);
    const i = cles.indexOf(cle), j = i + dir;
    if (j < 0 || j >= cles.length) return;
    [cles[i], cles[j]] = [cles[j], cles[i]];
    persister({ blocs: blocs.map(x => x.id), cartes: { ...(ordre?.cartes || {}), [rubId]: cles } });
  };

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={titre} iconeTitre={emoji}>
        {enfants}
        {!!cleOrdre && (reorg ? (
          <>
            <TouchableOpacity hitSlop={HIT} style={{ marginRight: 14 }}
              onPress={() => { persister({ blocs: [], cartes: {} }); setSel(null); }}>
              <Text style={s.reinitTxt}>{t('famille.reorgReinit')}</Text>
            </TouchableOpacity>
            <TouchableOpacity hitSlop={HIT} style={s.termineBtn}
              onPress={() => { setReorg(false); setSel(null); }}>
              <Text style={s.termineTxt}>{t('famille.reorgTermine')}</Text>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity hitSlop={HIT} onPress={() => setReorg(true)}
            accessibilityLabel={t('famille.reorganiser')}>
            <Text style={s.hIc}>↕</Text>
          </TouchableOpacity>
        ))}
      </PageHeader>

      {reorg && (
        <View style={s.banner}><Text style={s.bannerTxt}>ℹ️ {t('famille.reorgAide')}</Text></View>
      )}

      <ScrollView contentContainerStyle={s.corps}>
        {blocs.map((r, ri) => {
          // Accordéon par rubrique : replié = accessibles seulement. En
          // réorganisation tout reste visible, sinon on déplacerait à l'aveugle.
          const nbVerrou = r.tuiles.filter(m => !m.actif).length;
          const ouvert = reorg || !!ouverts[r.id];
          const affiches = ouvert ? r.tuiles : r.tuiles.filter(m => m.actif);
          if (!affiches.length && !nbVerrou) return null;
          return (
            <View key={r.id} style={s.section}>
              <View style={s.sectionHead}>
                {reorg && (
                  <View style={s.blocMove}>
                    <TouchableOpacity hitSlop={HIT} disabled={ri === 0} onPress={() => deplacerBloc(r.id, -1)}
                      style={[s.blocBtn, ri === 0 && s.blocBtnOff]}>
                      <Text style={[s.blocTxt, ri === 0 && s.blocTxtOff]}>▲</Text>
                    </TouchableOpacity>
                    <TouchableOpacity hitSlop={HIT} disabled={ri === blocs.length - 1} onPress={() => deplacerBloc(r.id, 1)}
                      style={[s.blocBtn, ri === blocs.length - 1 && s.blocBtnOff]}>
                      <Text style={[s.blocTxt, ri === blocs.length - 1 && s.blocTxtOff]}>▼</Text>
                    </TouchableOpacity>
                  </View>
                )}
                <TouchableOpacity style={s.sectionTitreZone} activeOpacity={reorg ? 1 : 0.6} delayLongPress={350}
                  onLongPress={() => setReorg(true)}
                  onPress={() => { if (!reorg && nbVerrou > 0) setOuverts(o => ({ ...o, [r.id]: !o[r.id] })); }}>
                  <Text style={s.sectionTitre}>{r.titre}</Text>
                  {!reorg && nbVerrou > 0 && (
                    <Text style={s.rubToggleTxt}>
                      {ouvert ? `${t('famille.reduire')} ▲` : `${t('famille.plusADebloquer', { n: nbVerrou })} ▼`}
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
              <View style={s.sectionGrid}>
                {affiches.map((m, idx) => (
                  <CarteHub key={m.cle} tuile={m} reorg={reorg} selected={reorg && sel === m.cle}
                    idx={idx} total={affiches.length}
                    onDeplacer={(dir) => deplacerCarte(r.id, m.cle, dir)}
                    onPress={() => (reorg ? setSel(x => (x === m.cle ? null : m.cle)) : m.onPress?.())} />
                ))}
                {/* Cale : une grille impaire ne doit pas étirer la dernière carte. */}
                {affiches.length % 2 === 1 && <View style={s.cardCale} />}
              </View>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 24 },
  hIc: { fontSize: 19, color: UI.accent, fontWeight: '800', marginLeft: 8 },
  reinitTxt: { fontSize: 14, color: UI.accent, fontWeight: '700' },
  termineBtn: { backgroundColor: UI.accent, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 6 },
  termineTxt: { color: '#fff', fontSize: 14, fontWeight: '800' },
  banner: { flexDirection: 'row', backgroundColor: UI.accentTint, marginHorizontal: 12, borderRadius: 12,
            paddingHorizontal: 12, paddingVertical: 9, marginBottom: 4 },
  bannerTxt: { flex: 1, fontSize: 12, color: UI.accent, fontWeight: '600', lineHeight: 16 },
  section: { marginBottom: 6 },
  sectionHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sectionTitreZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  sectionTitre: { fontSize: 14, fontWeight: '800', color: UI.accent, marginBottom: 8, marginLeft: 2,
                  textTransform: 'uppercase', letterSpacing: 0.3 },
  rubToggleTxt: { fontSize: 12, fontWeight: '700', color: UI.accent, marginBottom: 8 },
  blocMove: { flexDirection: 'row', gap: 3, marginBottom: 8 },
  blocBtn: { backgroundColor: UI.accentTint, borderRadius: 8, paddingHorizontal: 7, paddingVertical: 3 },
  blocBtnOff: { backgroundColor: UI.champ },
  blocTxt: { fontSize: 12, color: UI.accent, fontWeight: '800' },
  blocTxtOff: { color: UI.faint },
  sectionGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  cardCale: { width: '48.5%' },
});
